<template>
  <v-autocomplete
    v-model="selectedUnit"
    :items="units"
    :search-input.sync="searchInput"
    item-text="text"
    item-value="value"
    placeholder="Search for a Unit #, Smart Zone, or Facility"
    prepend-icon="mdi-magnify"
    return-object
    :no-data-text="noDataText"
    v-bind="$attrs"
    v-on="$listeners"
    @change="handleSelection"
  />
</template>

<script>
import useUser from "@components/authentication/useUser";
import { UNIT_WITH_PARENTS } from "@components/manage_tasks/graphql";
import { ref, watch } from "vue";

export default {
  props: {
    value: {
      type: String,
      default: "",
    },
    defaultValue: {
      type: String,
      default: "",
    },
  },
  setup(props, { emit }) {
    const { defaultOrganization } = useUser();
    const searchInput = ref("");
    const selectedUnit = ref(null);
    const units = ref([]);
    const search = ref("");
    const noDataText = ref("Type in a Unit #, Smart Zone, or Facility");

    watch(searchInput, (newValue) => {
      if (!props.defaultValue) {
        if (newValue !== selectedUnit.value?.text) {
          search.value = newValue;
        }
      }
    });

    const handleSelection = () => {
      // Clear searchInput when an item is selected
      searchInput.value = "";
      emit("input", selectedUnit.value.value);
      emit("parentFacilityId", selectedUnit.value.parentFacility);
    };

    return {
      defaultOrganization,
      searchInput,
      selectedUnit,
      units,
      search,
      handleSelection,
      noDataText,
    };
  },
  apollo: {
    units: {
      query: UNIT_WITH_PARENTS,
      variables() {
        return {
          id: this.defaultOrganization.id,
          types: ["zone", "facility"],
          types: this.facilityOnly ? ["facility"] : ["zone", "facility"],
          searchText: this.search || this.defaultValue,
        };
      },
      update(data) {
        let mappedUnits = data.organizationDescendants.map((unit) => ({
          text:
            unit.type === "facility"
              ? `${unit.name}${
                  unit.facility[0]?.code ? " - " + unit.facility[0].code : ""
                }`
              : `${unit.name} - ${unit.facility[0]?.name || ""} - ${
                  unit.facility[0]?.code || ""
                }`,
          value: unit.id,
          parentFacility: unit.facility[0]?.id,
          isFacility: unit.type === "facility",
        }));
        // If facilityOnly, filter to only facilities
        if (this.facilityOnly) {
          mappedUnits = mappedUnits.filter((unit) => unit.isFacility);
        }

        this.units = mappedUnits;
        // If we have a default value it will select matching units
        if (this.defaultValue && !this.selectedUnit) {
          const matchingUnit = mappedUnits.find((unit) =>
            unit.text.toLowerCase().includes(this.defaultValue.toLowerCase())
          );
          if (matchingUnit) {
            this.selectedUnit = matchingUnit.value;
            this.$emit("input", matchingUnit.value);
            this.$emit("parentFacilityId", matchingUnit.parentFacility);
          }
        }

        return mappedUnits;
      },
      skip() {
        return !this.search && !this.defaultValue;
      },
      debounce: 300,
    },
  },
};
</script>

<style lang="scss" scoped></style>
